/**
 * 记忆管理页（T13）可见部件元数据。
 *
 * 与「会话列表页」同一约定：本文件声明每个可见部件的 id / 角色 / 中文名 /
 * 390x844 逻辑坐标 / zIndex / props / notes，页面组件通过 `memoryMeta()` 取
 * 同一份数据用 `useComponentMeta()` 登记进全局注册表；静态模板条目（如
 * `memory.item`）供安卓迁移清单使用，运行时按记忆 id 展开为 `memory.item.<id>`。
 *
 * 坐标原点为画布左上角，页面内容区自 y=24（状态栏安全区）起。
 */
import type { ComponentMeta } from '../../core/types';
import { buildComponentMeta, type BoxLayout, type ComponentMetaInput } from '../../ui/meta';

/** 内容区顶部（状态栏安全区之下，与 app.content 对齐）。 */
export const PAGE_TOP = 24;
/** 内容区底部（底部导航条之上）。 */
export const PAGE_BOTTOM = 768;

export const HEADER_TOP = 24;
export const HEADER_HEIGHT = 56;
export const AUTO_MEMORY_TOP = 80;
export const AUTO_MEMORY_HEIGHT = 84;
export const BASE_FILTER_TOP = 80;
export const INJECTION_MODE_TOP = AUTO_MEMORY_TOP + AUTO_MEMORY_HEIGHT;
export const INJECTION_MODE_HEIGHT = 96;
/** Canonical manifest layout: the admin-enabled state with the automatic-memory row. */
export const FILTER_TOP = INJECTION_MODE_TOP + INJECTION_MODE_HEIGHT;
export const FILTER_HEIGHT = 56;
export const LIST_TOP = FILTER_TOP + FILTER_HEIGHT;
export const LIST_HEIGHT = PAGE_BOTTOM - LIST_TOP;
export const ROW_HEIGHT = 96;

/** 重要度可选值（1..5）。 */
export const IMPORTANCE_LEVELS = [1, 2, 3, 4, 5] as const;

/** 第 index 条记忆卡片的声明布局（行高 96dp）。 */
export function memoryItemLayout(index: number, listTop: number = LIST_TOP): BoxLayout {
  return { x: 0, y: listTop + index * ROW_HEIGHT, width: 390, height: ROW_HEIGHT };
}

export function memoryItemContentLayout(item: BoxLayout): BoxLayout {
  return { x: 16, y: item.y + 12, width: 288, height: 24 };
}

export function memoryItemBadgeLayout(item: BoxLayout): BoxLayout {
  return { x: 326, y: item.y + 12, width: 48, height: 24 };
}

export function memoryItemTagsLayout(item: BoxLayout): BoxLayout {
  return { x: 16, y: item.y + 40, width: 260, height: 20 };
}

export function memoryItemMetaLayout(item: BoxLayout): BoxLayout {
  return { x: 16, y: item.y + 70, width: 250, height: 18 };
}

export function memoryItemEditLayout(item: BoxLayout): BoxLayout {
  return { x: 286, y: item.y + 50, width: 44, height: 44 };
}

export function memoryItemDeleteLayout(item: BoxLayout): BoxLayout {
  return { x: 334, y: item.y + 50, width: 44, height: 44 };
}

/** 构造一条「记忆页」组件元数据输入（统一补 page 字段）。 */
export function memoryMetaInput(
  id: string,
  label: string,
  role: ComponentMeta['role'],
  layout: BoxLayout,
  options: { zIndex?: number; props?: string[]; notes?: string } = {},
): ComponentMetaInput {
  return {
    id,
    page: 'memory',
    label,
    role,
    layout,
    zIndex: options.zIndex ?? 0,
    ...(options.props === undefined ? {} : { props: options.props }),
    ...(options.notes === undefined ? {} : { notes: options.notes }),
  };
}

const ITEM_TEMPLATE = memoryItemLayout(0);

export const MEMORY_COMPONENTS: ComponentMetaInput[] = [
  memoryMetaInput('page.memory.root', '记忆管理页容器', 'container', {
    x: 0,
    y: PAGE_TOP,
    width: 390,
    height: PAGE_BOTTOM - PAGE_TOP,
  }, { zIndex: 2, notes: '页面根容器（含顶部 24dp 状态栏安全区偏移）；安卓用 Fragment 根 View' }),
  memoryMetaInput('memory.header', '记忆页头部', 'container', {
    x: 0,
    y: HEADER_TOP,
    width: 390,
    height: HEADER_HEIGHT,
  }, { zIndex: 3, notes: '标题 + 新增按钮容器；安卓用 MaterialToolbar' }),
  memoryMetaInput('memory.header.title', '页面标题：记忆', 'text', {
    x: 16,
    y: HEADER_TOP + 12,
    width: 220,
    height: 32,
  }, { zIndex: 4, notes: '安卓用 Toolbar 标题' }),
  memoryMetaInput('memory.newButton', '新增记忆', 'button', {
    x: 330,
    y: HEADER_TOP + 8,
    width: 44,
    height: 44,
  }, { zIndex: 4, props: ['disabled'], notes: '安卓用 FloatingActionButton（mini）' }),
  memoryMetaInput('memory.resetButton', '重置数据', 'button', {
    x: 278,
    y: HEADER_TOP + 8,
    width: 48,
    height: 44,
  }, { zIndex: 4, props: ['disabled'], notes: '危险操作：调用 clearDatabase() 清空全部本地数据，点击后二次确认' }),
  memoryMetaInput('memory.autoMemory.bar', '自动记忆栏（管理员开启后显示）', 'container', {
    x: 0,
    y: AUTO_MEMORY_TOP,
    width: 390,
    height: AUTO_MEMORY_HEIGHT,
  }, { zIndex: 3, notes: '管理员后台开启后，用户端才显示此栏；关闭时整个栏隐藏' }),
  memoryMetaInput('memory.autoMemory.toggle', '自动记忆开关', 'button', {
    x: 16,
    y: AUTO_MEMORY_TOP + 10,
    width: 120,
    height: 32,
  }, { zIndex: 4, props: ['checked'], notes: '胶囊开关；安卓用 Switch + 胶囊背景' }),
  memoryMetaInput('memory.autoMemory.modelButton', '自动记忆模型选择', 'button', {
    x: 146,
    y: AUTO_MEMORY_TOP + 10,
    width: 228,
    height: 32,
  }, { zIndex: 4, notes: '打开提供商 / 模型选择弹窗，支持官方与自定义提供商' }),
  memoryMetaInput('memory.autoMemory.hint', '自动记忆说明', 'text', {
    x: 16,
    y: AUTO_MEMORY_TOP + 44,
    width: 358,
    height: 34,
  }, { zIndex: 4, notes: '提示消耗会增加，记忆数据仅保存在本地' }),
  memoryMetaInput('memory.injectionMode.bar', '注入模式栏', 'container', {
    x: 0,
    y: INJECTION_MODE_TOP,
    width: 390,
    height: INJECTION_MODE_HEIGHT,
  }, { zIndex: 3, notes: '记忆注入模式选择；自动记忆栏隐藏时上移到头部下方' }),
  memoryMetaInput('memory.injectionMode.label', '注入模式标签', 'text', {
    x: 16,
    y: INJECTION_MODE_TOP + 8,
    width: 84,
    height: 40,
  }, { zIndex: 4, notes: '与下拉菜单同一行，垂直居中' }),
  memoryMetaInput('memory.injectionMode.select', '注入模式下拉菜单', 'input', {
    x: 104,
    y: INJECTION_MODE_TOP + 8,
    width: 270,
    height: 40,
  }, {
    zIndex: 4,
    props: ['value', 'options'],
    notes: '安卓用 Spinner；选项为关键词注入 / 自动注入 / 持续注入 / 关闭注入',
  }),
  memoryMetaInput('memory.injectionMode.hint', '注入模式说明', 'text', {
    x: 16,
    y: INJECTION_MODE_TOP + 54,
    width: 358,
    height: 34,
  }, { zIndex: 4, notes: '随所选模式变化的两行说明文字' }),
  memoryMetaInput('memory.filterBar', '记忆筛选栏', 'container', {
    x: 0,
    y: FILTER_TOP,
    width: 390,
    height: FILTER_HEIGHT,
  }, { zIndex: 3, notes: '标签筛选 + 排序切换容器；安卓用横向 ChipGroup + 排序按钮' }),
  memoryMetaInput('memory.filter.tags', '标签筛选列表', 'list', {
    x: 8,
    y: FILTER_TOP + 10,
    width: 268,
    height: 36,
  }, { zIndex: 4, props: ['selectedTag'], notes: '横向滚动标签芯片；安卓用 HorizontalScrollView + Chip' }),
  memoryMetaInput('memory.filter.sortButton', '排序切换（重要度/更新时间）', 'button', {
    x: 284,
    y: FILTER_TOP + 6,
    width: 98,
    height: 44,
  }, { zIndex: 4, props: ['sortBy'], notes: '安卓用 MaterialButton（toggle）' }),
  memoryMetaInput('memory.list', '记忆列表', 'list', {
    x: 0,
    y: LIST_TOP,
    width: 390,
    height: LIST_HEIGHT,
  }, { zIndex: 3, props: ['items'], notes: '安卓用 RecyclerView；卡片行高 96dp' }),
  memoryMetaInput('memory.item', '记忆卡片（列表项模板）', 'container', ITEM_TEMPLATE, {
    zIndex: 4,
    props: ['content', 'tags', 'importance', 'recallCount'],
    notes: '列表项模板；运行时按记忆 id 登记为 memory.item.<id>',
  }),
  memoryMetaInput('memory.item.content', '记忆内容', 'text', memoryItemContentLayout(ITEM_TEMPLATE), {
    zIndex: 5,
    notes: '单行省略号',
  }),
  memoryMetaInput('memory.item.badge', '重要度徽标', 'text', memoryItemBadgeLayout(ITEM_TEMPLATE), {
    zIndex: 5,
    notes: '格式：重要度 N',
  }),
  memoryMetaInput('memory.item.tags', '记忆标签', 'text', memoryItemTagsLayout(ITEM_TEMPLATE), {
    zIndex: 5,
    notes: '以 # 前缀串联',
  }),
  memoryMetaInput('memory.item.meta', '召回次数与更新时间', 'text', memoryItemMetaLayout(ITEM_TEMPLATE), {
    zIndex: 5,
    notes: '格式：召回 N 次 · 时间',
  }),
  memoryMetaInput('memory.item.editButton', '编辑记忆', 'button', memoryItemEditLayout(ITEM_TEMPLATE), {
    zIndex: 6,
    notes: '触摸目标 44x44',
  }),
  memoryMetaInput('memory.item.deleteButton', '删除记忆', 'button', memoryItemDeleteLayout(ITEM_TEMPLATE), {
    zIndex: 6,
    notes: '触摸目标 44x44；点击后二次确认',
  }),
  memoryMetaInput('memory.empty', '记忆列表空状态', 'container', {
    x: 0,
    y: 376,
    width: 390,
    height: 160,
  }, { zIndex: 3, notes: '本地没有记忆时显示' }),
  memoryMetaInput('memory.empty.title', '空状态主文案', 'text', {
    x: 0,
    y: 396,
    width: 390,
    height: 32,
  }, { zIndex: 4, notes: '居中' }),
  memoryMetaInput('memory.empty.hint', '空状态说明', 'text', {
    x: 0,
    y: 432,
    width: 390,
    height: 24,
  }, { zIndex: 4, notes: '居中，次要文本色' }),
  memoryMetaInput('memory.autoMemory.picker.backdrop', '自动记忆模型选择遮罩', 'background', {
    x: 0,
    y: PAGE_TOP,
    width: 390,
    height: PAGE_BOTTOM - PAGE_TOP,
  }, { zIndex: 50, notes: '点击关闭' }),
  memoryMetaInput('memory.autoMemory.picker.dialog', '自动记忆模型选择弹窗', 'modal', {
    x: 24,
    y: 168,
    width: 342,
    height: 372,
  }, { zIndex: 51, notes: '选择提供商与模型；支持跟随对话、官方模型和自定义提供商' }),
  memoryMetaInput('memory.autoMemory.picker.title', '自动记忆模型选择标题', 'text', {
    x: 44,
    y: 184,
    width: 302,
    height: 28,
  }, { zIndex: 52, notes: '弹窗标题；安卓用 AlertDialog 标题 TextView' }),
  memoryMetaInput('memory.autoMemory.picker.providerLabel', '提供商字段标签', 'text', {
    x: 44,
    y: 220,
    width: 302,
    height: 20,
  }, { zIndex: 52, notes: '表单字段说明' }),
  memoryMetaInput('memory.autoMemory.picker.providerSelect', '自动记忆提供商', 'input', {
    x: 44,
    y: 242,
    width: 302,
    height: 44,
  }, { zIndex: 52, props: ['value', 'options'], notes: '安卓用 Spinner；选项含跟随对话、官方模型与自定义提供商' }),
  memoryMetaInput('memory.autoMemory.picker.modelLabel', '模型字段标签', 'text', {
    x: 44,
    y: 298,
    width: 302,
    height: 20,
  }, { zIndex: 52, notes: '表单字段说明' }),
  memoryMetaInput('memory.autoMemory.picker.modelSelect', '自动记忆模型', 'input', {
    x: 44,
    y: 320,
    width: 302,
    height: 44,
  }, { zIndex: 52, props: ['value', 'options'], notes: '安卓用 Spinner；模型列表随所选提供商变化' }),
  memoryMetaInput('memory.autoMemory.picker.hint', '自动记忆模型说明', 'text', {
    x: 44,
    y: 376,
    width: 302,
    height: 66,
  }, { zIndex: 52, notes: '说明计费方式与不可用原因' }),
  memoryMetaInput('memory.autoMemory.picker.cancelButton', '取消自动记忆模型选择', 'button', {
    x: 44,
    y: 468,
    width: 140,
    height: 44,
  }, { zIndex: 52, notes: '次按钮' }),
  memoryMetaInput('memory.autoMemory.picker.saveButton', '保存自动记忆模型', 'button', {
    x: 206,
    y: 468,
    width: 140,
    height: 44,
  }, { zIndex: 52, props: ['disabled'], notes: '主按钮；未选择模型时禁用' }),
  memoryMetaInput('memory.editor.backdrop', '新增/编辑记忆遮罩', 'background', {
    x: 0,
    y: PAGE_TOP,
    width: 390,
    height: PAGE_BOTTOM - PAGE_TOP,
  }, { zIndex: 20, notes: '半透明遮罩，点击关闭' }),
  memoryMetaInput('memory.editor.dialog', '新增/编辑记忆弹窗', 'modal', {
    x: 24,
    y: 160,
    width: 342,
    height: 412,
  }, { zIndex: 21, props: ['mode'], notes: '新增或编辑记忆表单' }),
  memoryMetaInput('memory.editor.title', '弹窗标题：新增/编辑记忆', 'text', {
    x: 44,
    y: 176,
    width: 302,
    height: 28,
  }, { zIndex: 22, notes: '安卓用 AlertDialog 标题' }),
  memoryMetaInput('memory.editor.contentLabel', '内容字段标签', 'text', {
    x: 44,
    y: 214,
    width: 302,
    height: 20,
  }, { zIndex: 22, notes: '表单字段说明' }),
  memoryMetaInput('memory.editor.contentInput', '记忆内容输入', 'input', {
    x: 44,
    y: 236,
    width: 302,
    height: 110,
  }, { zIndex: 22, props: ['value', 'multiline'], notes: '安卓用多行 EditText' }),
  memoryMetaInput('memory.editor.tagsLabel', '标签字段标签', 'text', {
    x: 44,
    y: 356,
    width: 302,
    height: 20,
  }, { zIndex: 22, notes: '说明用逗号分隔' }),
  memoryMetaInput('memory.editor.tagsInput', '标签输入', 'input', {
    x: 44,
    y: 378,
    width: 302,
    height: 44,
  }, { zIndex: 22, props: ['value'], notes: '逗号分隔的标签，安卓用单行 EditText' }),
  memoryMetaInput('memory.editor.importanceLabel', '重要度字段标签', 'text', {
    x: 44,
    y: 430,
    width: 150,
    height: 20,
  }, { zIndex: 22, notes: '取值范围 1..5' }),
  memoryMetaInput('memory.editor.importanceSelect', '重要度选择', 'input', {
    x: 44,
    y: 452,
    width: 302,
    height: 44,
  }, { zIndex: 22, props: ['value', 'options'], notes: '安卓用 Spinner / Slider' }),
  memoryMetaInput('memory.editor.saveButton', '保存记忆', 'button', {
    x: 44,
    y: 512,
    width: 140,
    height: 44,
  }, { zIndex: 22, props: ['disabled'], notes: '主按钮' }),
  memoryMetaInput('memory.editor.cancelButton', '取消编辑', 'button', {
    x: 206,
    y: 512,
    width: 140,
    height: 44,
  }, { zIndex: 22, notes: '次按钮' }),
  memoryMetaInput('memory.confirm.backdrop', '删除确认遮罩', 'background', {
    x: 0,
    y: PAGE_TOP,
    width: 390,
    height: PAGE_BOTTOM - PAGE_TOP,
  }, { zIndex: 30, notes: '删除二次确认遮罩' }),
  memoryMetaInput('memory.confirm.dialog', '删除确认弹窗', 'modal', {
    x: 39,
    y: 320,
    width: 312,
    height: 180,
  }, { zIndex: 31, notes: '二次确认对话框' }),
  memoryMetaInput('memory.confirm.title', '删除确认标题', 'text', {
    x: 63,
    y: 336,
    width: 264,
    height: 28,
  }, { zIndex: 32, notes: '删除记忆二次确认标题；安卓用 AlertDialog 标题 TextView' }),
  memoryMetaInput('memory.confirm.message', '删除确认说明', 'text', {
    x: 63,
    y: 372,
    width: 264,
    height: 44,
  }, { zIndex: 32, notes: '提示不可恢复' }),
  memoryMetaInput('memory.confirm.cancelButton', '取消删除', 'button', {
    x: 63,
    y: 436,
    width: 120,
    height: 44,
  }, { zIndex: 32, notes: '次按钮' }),
  memoryMetaInput('memory.confirm.confirmButton', '确认删除', 'button', {
    x: 207,
    y: 436,
    width: 120,
    height: 44,
  }, { zIndex: 32, props: ['disabled'], notes: '危险按钮' }),
  memoryMetaInput('memory.resetConfirm.backdrop', '重置数据确认遮罩', 'background', {
    x: 0,
    y: PAGE_TOP,
    width: 390,
    height: PAGE_BOTTOM - PAGE_TOP,
  }, { zIndex: 40, notes: '清空全部本地数据的二次确认遮罩' }),
  memoryMetaInput('memory.resetConfirm.dialog', '重置数据确认弹窗', 'modal', {
    x: 39,
    y: 320,
    width: 312,
    height: 180,
  }, { zIndex: 41, notes: '二次确认：调用 clearDatabase() 清空全部本地数据' }),
  memoryMetaInput('memory.resetConfirm.title', '重置数据确认标题', 'text', {
    x: 63,
    y: 336,
    width: 264,
    height: 28,
  }, { zIndex: 42, notes: '重置数据二次确认标题；安卓用 AlertDialog 标题 TextView' }),
  memoryMetaInput('memory.resetConfirm.message', '重置数据确认说明', 'text', {
    x: 63,
    y: 372,
    width: 264,
    height: 44,
  }, { zIndex: 42, notes: '提示清空对话/记忆等全部本地数据且不可恢复' }),
  memoryMetaInput('memory.resetConfirm.cancelButton', '取消重置', 'button', {
    x: 63,
    y: 436,
    width: 120,
    height: 44,
  }, { zIndex: 42, notes: '次按钮' }),
  memoryMetaInput('memory.resetConfirm.confirmButton', '确认重置', 'button', {
    x: 207,
    y: 436,
    width: 120,
    height: 44,
  }, { zIndex: 42, props: ['disabled'], notes: '危险按钮' }),
  memoryMetaInput('memory.errorBanner', '错误提示条', 'container', {
    x: 12,
    y: 136,
    width: 366,
    height: 36,
  }, { zIndex: 12, notes: '读取/写入失败时显示' }),
  memoryMetaInput('memory.errorBanner.message', '错误提示文案', 'text', {
    x: 20,
    y: 144,
    width: 350,
    height: 20,
  }, { zIndex: 13, notes: '危险色文本' }),
];

const COMPONENT_INDEX = new Map(MEMORY_COMPONENTS.map((meta) => [meta.id, meta]));

/** 按 id 取声明式元数据输入。 */
export function memoryMeta(id: string): ComponentMetaInput {
  const found = COMPONENT_INDEX.get(id);
  if (found === undefined) throw new Error(`[memory] 未登记的部件：${id}`);
  return found;
}

/** 按 id 取声明布局（页面内联定位与清单坐标共用一份数据）。 */
export function memoryLayout(id: string): BoxLayout {
  return memoryMeta(id).layout;
}

/** 按 id 构造完整 ComponentMeta，供 `useComponentMeta()` 登记。 */
export function memoryComponentMeta(id: string): ComponentMeta {
  return buildComponentMeta(memoryMeta(id));
}
